<template>
  <div class="exam-grid-list-root" v-loader.opacity="loading">
    <BaseGridTable class="no-border" v-if="table_exam && session" :tabledata="table_exam"
      @item-opened="$emit('item-opened')" v-bind="$attrs">
      <template v-slot:column_name="{ item }">
        <div class="patient-box" :data-exam-id="item.id">
          <div class="patient-info-actions">
            <a class="exam-patient-name" :class="{'anonymous': isJokerPatient(item)}" href="#" @click.prevent.stop="handlePatientClick(item)">
              <IconPlusSmall v-if="isJokerPatient(item)"/>
              <span>{{ isJokerPatient(item) ? $t('patient.addPatient') : item.patient.fullName }}</span>
            </a>

            <p class="date">
              <IconCalendar/>
              <span>{{ formatDate(item.date) }}</span>
            </p>
          </div>

          <span v-if="showMutirao" style="display: flex; align-items: center;">
            <i v-show="item.referred > 0" class="fa" :class="item.referred == 1 ? 'fa-tint' : 'fa-check-circle-o'" aria-hidden="true"></i>
          </span>
        </div>
      </template>

      <template v-slot:column_captures="{ item }">
        <lightgallery-vue class="image-info-box grid-wrap-between ease-all" :class="`zoom-level-${scale}`" :ref="`lGallery_${item.uuid}`"
          :id="`lightgallery_${item.uuid}`" :key="item.uuid" :settings="lightGallerySettings" :onInit="onInit"
          :onAfterOpen="() => extraCustomization(item)" :onBeforeSlide="onBeforeSlide" :onAfterClose="onLgClose"
          v-if="item.examImages.length > 0" @click.stop.prevent="prevent">

          <template v-for="(image, imageIndex) in getGalleryImages(item)" :key="imageIndex">
            <a v-if="image.type.endsWith('VIDEO')" onclick="return false" class="thumbnail-box loading-eye" :class="image.type" :key="`video_${imageIndex}`" data-lg-size="1280-720"
              :data-video="getVideoSource(image)" data-download-url="" data-sub-html="<h4></h4><p></p>" :data-test="`anchorVideo_${imageIndex}`"
              @mouseenter="onVideoHoverStart(item, imageIndex)" @mouseleave="onVideoHoverEnd(item, imageIndex)">
              <video v-if="playingVideoIndex === videoKey(item, imageIndex)" class="lg-selector ease-all hover-preview-video thumbnail-media" :class="{ 'eyer2-image': image.eyerVersion > 0 }" :src="`${data_path}/${image.name}`" :ref="el => playHoverVideo(el)" autoplay muted loop playsinline></video>
              <img class="lg-selector ease-all loading-eye thumbnail-media" :class="{ 'eyer2-image': image.eyerVersion > 0 }" :src="video_thumb_path ? (video_thumb_path + '/' + image.name) : require('@/assets/images/default-video2.png')" loading="lazy" decoding="async" alt="" @load="removeLoading($event)" @error="video_thumb_path && $utils.enableDownloadOnError($event, image, doDownload)"/>

              <div class="image-badge" v-if="image.imageLaterality">
                <p class="image-badge__laterality">
                  {{ image.imageLaterality === 'R' ? $t('od') : $t('os') }}
                </p>
              </div>

              <div class="icon-video">
                <i class="fa fa-play-circle-o"></i>
              </div>

              <div class="dropdown">
                <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" @click.stop.prevent>
                  <IconDot class="icon-dot" />
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li class="dropdown-action-item">
                    <a class="dropdown-item" :data-test="`btnDownloadImage_${imageIndex}`" @click.stop.prevent="doDownload(image, image.type.endsWith('VIDEO'))">
                      <IconDownload/>
                      {{ $t('download') }}
                    </a>
                  </li>
                </ul>
              </div>
            </a>

            <a v-else class="thumbnail-box loading-eye" onclick="return false" :class="image.type"
              :data-laterality="image.imageLaterality || null"
              :href="data_path + '/' + image.name" :data-test="`anchorImage_${imageIndex}`">

              <img class="lg-selector ease-all loading-eye thumbnail-media" :class="{ 'eyer2-image': image.eyerVersion > 0 }"
                    :src="thumb_path + '/' + image.name" loading="lazy" decoding="async" alt="" @load="removeLoading($event)" @error="$utils.enableDownloadOnError($event, image, doDownload)" />
              <IconDownload class="thumbnail-not-found-icon"/>

              <div class="image-badge">
                <span class="image-badge__ai-status" v-if="hasTypeStatusBadge(image.type)" :class="computeImageAiClass(image, item)"></span>

                <p class="image-badge__laterality" v-if="image.imageLaterality">
                  {{ image.imageLaterality === 'R' ? $t('od') : $t('os') }}
                </p>

                <p class="image-badge__ai" v-if="false">
                  AI
                  <span class="image-badge__ai-status" :class="computeImageAiClass(image, item)"></span>
                </p>

                <p class="image-badge__type" v-if="image.type !== 'FFA'">
                  {{ badgeTypeLabel(image.type) }}
                </p>

                <p class="image-badge__ffa" v-if="image.type === 'FFA'">
                  {{ badgeTypeLabel(image.type) }}
                  <IconClock/>
                  {{ formatFaElapsedTime(image) }}
                </p>
              </div>

              <div class="dropdown">
                <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" @click.stop.prevent>
                  <IconDot class="icon-dot" />
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li class="dropdown-action-item">
                    <a class="dropdown-item" :data-test="`btnDownloadImage_${imageIndex}`" @click.stop.prevent="doDownload(image, image.type == 'STEREO')">
                      <IconDownload/>
                      {{ $t('download') }}
                    </a>
                  </li>
                  <li class="dropdown-action-item" v-if="canSwitchLaterality(image)">
                    <a class="dropdown-item" data-test="btnToggleLaterality" @click.stop.prevent="openConfirmLateralitySwitchOffcanvas(image)">
                      <IconLaterality/>
                      {{ $t('examImages.laterality') }}
                    </a>
                  </li>
                </ul>
              </div>
            </a>
          </template>
        </lightgallery-vue>

        <div class="no-captures" v-else>
          <p>{{ $t('exam.noCaptures') }}</p>
        </div>
      </template>

      <template v-slot:column_content="{ item }">
        <div class="custom-box">
          <div class="">
            <!-- EYC-204 -->
            <!-- <div v-if="false"> -->
              <template v-if="hasUnifiedReportFeature && !isJokerPatient(item)">
                <div class="btn-group btn-group-mark" :key="item.uuid" v-if="!item.unifiedExam?.type" :class="{ 'disabled': shouldDisableActionButtons(item) }">
                  <p class="btn btn-outline-secondary" :data-test="`btnExamHasReport_${item.uuid}`">
                    <IconLamp/>
                    {{ $t('exam.examIs') }}
                  </p>
                  <button type="button" class="btn btn-outline-secondary" :class="{ 'disabled': shouldDisableActionButtons(item) }"
                     :data-test="`btnExamNoAlteration_${item.uuid}`" @click.stop.prevent="setAlteration('notAltered', item)" :disabled="shouldDisableActionButtons(item)">
                    <IconCheckBig/>
                    <span>{{ $t('exam.notAltered') }}</span>
                  </button>
                  <button type="button" class="btn btn-outline-secondary" :class="{ 'disabled': shouldDisableActionButtons(item) }"
                     :data-test="`btnExamHasAlteration_${item.uuid}`" @click.stop.prevent="setAlteration('altered', item)" :disabled="shouldDisableActionButtons(item)">
                    <IconWarningCircle/>
                    <span>{{ $t('exam.altered') }}</span>
                  </button>
                  <button type="button" class="btn btn-outline-secondary" :class="{ 'disabled': shouldDisableActionButtons(item) }"
                     :data-test="`btnExamNoQuality_${item.uuid}`" @click.stop.prevent="setAlteration('noQuality', item)" :disabled="shouldDisableActionButtons(item)">
                    <IconCross/>
                    <span>{{ $t('exam.noQuality') }}</span>
                  </button>
                </div>

                <div class="report-info-box" v-else>
                  <button class="btn btn-outline-secondary" :class="getAlterationClass(item.unifiedExam?.type)" type="button" name="button" v-if="!item.hasReport">
                    <IconPdfExam/>
                    {{ $t('tooltips.report') }}
                    <span>{{ $t(`exam.${item.unifiedExam?.type}`) }}</span>
                  </button>

                  <div class="dropdown" v-else>
                    <button class="btn btn-outline-secondary dropdown-toggle" :class="getAlterationClass(item.unifiedExam?.type)" type="button" data-bs-toggle="dropdown" aria-expanded="false" @click.prevent.stop="false">
                      <IconPdfExam/>
                      {{ $t('tooltips.report') }}
                      <span>{{ $t(`exam.${item.unifiedExam?.type}`) }}</span>
                    </button>
                    <ul class="dropdown-menu dropdown-exam-action">
                      <li class="dropdown-action-item">
                        <a class="dropdown-item" :data-test="`btnExamEditReport_${item.uuid}`" @click.stop.prevent="goToReport(item)">{{ $t('exam.editReport') }}</a>
                      </li>
                      <li class="dropdown-action-item">
                        <a class="dropdown-item" :data-test="`btnExamDownload_${item.uuid}`" @click.stop.prevent="doDownloadQuickReport(item)">{{ $t('exam.downloadExam') }}</a>
                      </li>
                      <li class="dropdown-action-item">
                        <a class="dropdown-item" :data-test="`btnExamHasReport_${item.uuid}`" @click.stop.prevent="doDownloadPDF(item)">{{ $t('exam.downloadReport') }}</a>
                      </li>
                    </ul>
                  </div>
                </div>
              </template>

              <!-- <template v-else-if="!hasUnifiedReportFeature && !isPaidService && !isJokerPatient(item)">
                <div class="btn-group btn-group-mark disabled blur" :key="item.uuid" @click.stop.prevent="toPlans()">
                  <p class="btn btn-outline-secondary" :data-test="`btnExamHasReportDisabled_${item.uuid}`">
                    <IconLamp/>
                    {{ $t('exam.examIs') }}
                  </p>
                  <button type="button" class="btn btn-outline-secondary disabled" :data-test="`btnExamNoAlterationDisabled_${item.uuid}`" disabled>
                    <IconCheckBig/>
                    <span>{{ $t('exam.notAltered') }}</span>
                  </button>
                  <button type="button" class="btn btn-outline-secondary disabled" :data-test="`btnExamHasAlterationDisabled_${item.uuid}`" disabled>
                    <IconWarningCircle/>
                    <span>{{ $t('exam.altered') }}</span>
                  </button>
                  <button type="button" class="btn btn-outline-secondary disabled" :data-test="`btnExamNoQualityDisabled_${item.uuid}`" disabled>
                    <IconCross/>
                    <span>{{ $t('exam.noQuality') }}</span>
                  </button>
                </div>
              </template> -->
            <!-- </div> -->

            <!-- <template v-else> -->
            <template v-else-if="!hasUnifiedReportFeature">
               <!-- v-if="(item.hasImage || item.hasReport) && item.hasNoAlteration" -->
              <div class="btn-group exam-actions-box" role="group" :aria-label="$t('exam.examActionsGroupLabel')" v-if="item.hasImage || item.hasReport">
                <button type="button" class="btn btn-outline-secondary" :disabled="isJokerPatient(item) ? true : !item.hasImage"
                  :data-title="$t('tooltips.download')" :data-test="`btnExamHasImage_${item.uuid}`" @click.stop.prevent="doDownloadQuickReport(item)">
                  <IconDownload />
                </button>
                <button type="button" class="btn btn-outline-secondary" :disabled="isJokerPatient(item)"
                  :data-title="$t('tooltips.report')" :data-test="`btnExamHasReport_${item.uuid}`" @click.stop.prevent="checkDownloadOrRoute(item)">
                  <IconPdf />
                </button>
              </div>
            </template>

            <div class="btn-group ai-status-group" role="group" aria-label="AI Assistant status">
              <button type="button" class="btn btn-outline-secondary ai-assistant-badge"
                v-if="item.scoreResult && hasAiAssistantFeature"
                :data-test="`btnGoToAiAssistant_${item.uuid}`"
                @click.stop.prevent="goToAiAssistant(item)">
                <img src="@/assets/images/ai-assistant/ai-assistant-logo.png" alt="AI Assistant" width="14"/>
                <span>AI ASSISTANT</span>
              </button>

              <div class="btn btn-outline-secondary ai-box" v-if="item.scoreResult && item.scoreResult.doubleCircle"
                @click.stop.prevent="hasAiAssistantFeature && goToAiAssistant(item)">
                <p>{{ aiStatusLabel(item) }}</p>
                <span :class="item.scoreResult.odStatus ?? ''"></span>
                <span :class="item.scoreResult.osStatus ?? ''"></span>
              </div>

              <div class="btn btn-outline-secondary ai-box" v-if="item.scoreResult && !item.scoreResult.doubleCircle"
                @click.stop.prevent="hasAiAssistantFeature && goToAiAssistant(item)">
                <p>{{ aiStatusLabel(item) }}</p>
                <span :class="item.scoreResult.odStatus ?? ''"></span>
              </div>
            </div>

            <div class="alteration-list" v-if="showMutirao && item?.scoreResult?.allAlterationText">
              <template v-for="text of item.scoreResult.allAlterationText" :key="text">
                <b v-if="text === 'mcRASE2_c1'">
                  {{ $t(text) }}
                </b>
                <span v-else>
                  {{ $t(text) }}
                </span>
              </template>
            </div>

            <button class="btn btn-outline-secondary btn-share" v-if="!isSpecialist && !isJokerPatient(item)" type="button" name="button"
              data-test="btnPatientShare" @click.prevent.stop="shareInformation(item)">
              <IconShare />
              <!-- <span>{{ $t('externalPatient.shareSimple') }}</span> -->
            </button>
          </div>

          <div class="action-group">
            <div class="btn-group btn-group-mark">
              <button class="btn btn-outline-secondary" type="button" name="button" :data-test="`btnExamAccess_${item.uuid}`" @click.prevent.stop="$emit('item-opened', {item: item})">
                {{ $t('exam.examAccess') }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <!-- <template v-slot:column_ai="{item}">

      </template> -->
    </BaseGridTable>

    <teleport to="body">
      <BasePatientAccessInfoOffcanvas ref="offcanvasPatientAccessInfo" />
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasConfirmLateralitySwitch'" ref="offcanvasGeneric"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.exam-grid-list-root {
  flex:                               1;
  display:                            flex;
  flex-direction:                     column;
  min-height:                         0;
}

:deep(.base-grid-table-root) {
  flex:                               1;
  display:                            flex;
  flex-direction:                     column;
  min-height:                         0;
}

:deep(.vtable-grid ul:not(.dropdown-exam-action) li:not(.dropdown-action-item)) {
  padding:                            10px 20px 20px;
}

:deep(.row-header) {
  @include                            transition(transform 0.3s ease-in-out);
  display:                            flex;
  flex-direction:                     row;
  flex-wrap:                          wrap;
  align-items:                        flex-start;
  gap:                                15px;
  width:                              calc(100% + 40px);
  margin:                             0 -20px;
  padding:                            10px 20px;
  position:                           sticky;
  top:                                0;
  z-index:                            103;

  &.is-stuck {
    z-index:                          102;
    background:                       linear-gradient(180deg,rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.0) 100%);
  }
}

.btn-group.exam-actions-box {
  height:                             37px;
}

.custom-box {
  width:                              100%;
  flex:                               1;

  > div {
    display:                          flex;
    // flex-wrap:                        wrap;
    align-items:                      flex-start;
    gap:                              15px;
    flex:                             1;
  }

  > .action-group {
    justify-content:                  flex-end;

    .btn-group-mark {
      .btn.btn-outline-secondary {
        height:                       37px;

        // Same mobile height as every other button in this stacked card layout — see
        // _table.scss's own .btn-group-mark/.ai-box/.date/.btn-share rules.
        @media (max-width: 991px) {
          height:                     28px;
        }

        &:nth-child(2) {
          @include                    border_radius(0);
        }

        &:only-child {
          @include                    border_radius(99px);
        }
      }
    }
  }
}

.fa {
  font-size:                          22px !important;
  align-self:                         center;
  color:                              var(--Brand-Primary, #1B294A);
}

.icon-video .fa {
  font-weight:                        normal !important;
  color:                              #B5B5B5;
  z-index:                            100;
}

.image-info-box {
  gap:                                10px;

  &.grid-wrap-between {
    justify-content:                  space-between;
    grid-template-columns:            repeat(auto-fill, 32.7%) !important;
    gap:                              5px;

    &.zoom-level-1 {
      grid-template-columns:          repeat(auto-fill, 9.40%) !important;

      .image-badge {
        height:                        16px;
        padding:                       2px 4px;
        gap:                           3px;

        &__laterality,
        &__ai,
        &__type,
        &__ffa {
          font-size:                   8px;
          letter-spacing:              0.8px;
        }

        &__laterality {
          padding-right:               3px;
        }

        &__ai,
        &__ffa {
          gap:                         3px;
        }

        &__ai-status {
          width:                       8px;
          height:                      8px;
        }

        &__ffa svg {
          width:                       8px;
          height:                      8px;
        }
      }
    }

    &.zoom-level-2 {
      grid-template-columns:          repeat(auto-fill, 16%) !important;
    }

    &.zoom-level-3 {
      grid-template-columns:          repeat(auto-fill, 24%) !important;
    }

    &.zoom-level-4 {
      grid-template-columns:          repeat(auto-fill, 49.70%) !important;
    }

    // RNFL_PANO spans a 2x2 block so the full stitched panorama reads as one large capture
    // instead of a cropped square — only worth it while thumbnails are small; at
    // zoom-level-4 (2 per row) each cell is already big enough on its own.
    &.zoom-level-1,
    &.zoom-level-2,
    &.zoom-level-3 {
      .thumbnail-box.RNFL_PANO {
        grid-row:                      span 2;
        grid-column:                   span 2;
        // .grid-wrap-between centers items (align-items: center), so without this the box
        // would keep its normal 1-row height and just float in the middle of its spanned
        // area instead of filling it.
        align-self:                    stretch;
      }
    }

    // RNFL_MASKED is kept in getGalleryImages() (and so in the DOM, as a lightGallery slide)
    // so it's reachable when the exam's pano is opened in the lightbox, but it's not meant to
    // be its own clickable thumbnail in the grid.
    .thumbnail-box.RNFL_MASKED {
      display:                        none;
    }
  }

  .thumbnail-box {
    margin:                           0;
    height:                           100%;

    img,
    video.lg-selector {
      width:                          100%;
      height:                         100%;
    }

    > .dropdown {
      .dropdown-toggle {
        width:                        23px;
        height:                       23px;
        min-height:                   unset;
        margin:                       0;

        svg {
          height:                     13px;
        }
      }

      .dropdown-item {
        font-size:                    var(--font-size-10);
      }
    }
  }
}

// TODO bruno: rever necessidade de todos os atributos
.thumbnail-box {
  position:                           relative;

  .thumbnail-not-found-icon {
    display:                          none;
  }

  &.image-failed {
    .thumbnail-not-found-icon {
      display:                        block;
      position:                       absolute;
      top:                            50%;
      left:                           50%;
      transform:                      translate(-50%, -50%);
      width:                          28px;
      height:                         28px;
      pointer-events:                 none;
      z-index:                        99;
    }

    .thumb-actions {
      display:                        none !important;
    }
  }

  .thumb-actions {
    position:                         absolute;
    bottom:                           8px;
    right:                            8px;
    z-index:                          10;
    width:                            auto;

    .btn-group {
      display:                        inline-flex;

      .btn {
        @include                      border_radius(90px);
        display:                      flex;
        align-items:                  center;
        justify-content:              center;
        min-width:                    32px !important;
        min-height:                   20px;
        padding:                      4px 15px !important;
        border:                       1px solid rgba(13, 110, 253, 0.1);

        &:hover {
          i {
            color:                    rgb(13, 110, 253);
          }
        }
        i.fa-download {
          font-size:                  13px !important;
          line-height:                1;
          width:                      12px;
          height:                     12px;
          vertical-align:             middle;
        }
      }

      .btn-download {
        min-width:                    24px !important;
        min-height:                   24px !important;
        padding:                      4px 6px !important;
      }
    }
  }
}

.rectract {
  .image-info-box.grid-wrap-between {
    grid-template-columns:             repeat(auto-fill, 24%) !important;
  }
}

.btn-group-mark.disabled p.btn.btn-outline-secondary {
  &,
  &:hover,
  &:focus {
    background:                        transparent !important;
    border:                            0;
  }
}

.btn-share {
  font-size:                           var(--font-size-10);
  display:                             flex;
  flex-direction:                      row;
  gap:                                 5px;
  max-height:                          37px;
  padding:                             6px 16px;
}

.btn {
  flex-wrap:                           nowrap;
  white-space:                         nowrap;
}

@media (max-width: 991px) {
  .custom-box {
    justify-content:                   center;
    flex-wrap:                         wrap;

    > div {
      flex:                            unset;
      flex-wrap:                       wrap;
    }
    > .btn {
      margin-left:                     unset;
    }
  }

  .ai-assistant-badge {
    gap:                              4px;

    img {
      width:                          11px;
    }

    span {
      font-size:                      8px;
      letter-spacing:                 1px;
    }
  }

  // height/padding for .ai-box itself are set (with !important) in _table.scss's
  // higher-specificity .vtable-grid ul:not()... block — overriding them here would be
  // dead weight, so this only touches what isn't already pinned down there.
  .ai-status-group .ai-box {
    max-width:                        130px;

    p {
      overflow:                       hidden;
      text-overflow:                  ellipsis;
      white-space:                    nowrap;
      min-width:                      0;
      font-size:                      8px;
      margin-right:                   6px;
    }

    span {
      width:                          10px;
      height:                         10px;
    }
  }

  .retract .image-info-box.grid-wrap-between,
  .image-info-box.grid-wrap-between {
    justify-content:                   space-between;
    grid-template-columns:             repeat(auto-fill, 32%) !important;

    &.zoom-level-1,
    &.zoom-level-2,
    &.zoom-level-3,
    &.zoom-level-4 {
      grid-template-columns:           repeat(auto-fill, 32%) !important;
    }

    // Mobile has no "already big enough" zoom level like desktop's zoom-level-4 (every
    // level collapses to the same 3-per-row width here), so RNFL_PANO spans 2x2 regardless
    // of zoom level. A standalone pano (no masked siblings) still renders as a plain
    // .thumbnail-box, so this rule stays.
    .thumbnail-box.RNFL_PANO {
      grid-row:                        span 2;
      grid-column:                     span 2;
      align-self:                      stretch;
    }

    // See desktop block: kept in the DOM as a lightGallery slide, not its own clickable
    // thumbnail in the grid.
    .thumbnail-box.RNFL_MASKED {
      display:                        none;
    }

    // Same shrink as desktop's zoom-level-1 (its densest tier) — mobile thumbnails are
    // small at every zoom level here, so the badge's default font-size overflows them too.
    .image-badge {
      height:                          16px;
      padding:                         2px 4px;
      gap:                             3px;

      &__laterality,
      &__ai,
      &__type,
      &__ffa {
        font-size:                     8px;
        letter-spacing:                0.8px;
      }

      &__laterality {
        padding-right:                 3px;
      }

      &__ai,
      &__ffa {
        gap:                           3px;
      }

      &__ai-status {
        width:                         8px;
        height:                        8px;
      }

      &__ffa svg {
        width:                         8px;
        height:                        8px;
      }
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .image-info-box.grid-wrap-between {
    grid-template-columns:             repeat(auto-fill, 32%) !important;

    &.zoom-level-4 {
      grid-template-columns:           repeat(auto-fill, 48%) !important;
    }
  }
}

.ai-status-group {
  align-self:                          flex-start;

  &.btn-group .ai-box {
    gap:                                unset;
  }

  &.btn-group .ai-box:only-child {
    @include                           border_radius(99px);
  }

  &.btn-group .btn:only-child {
    @include                           border_radius(99px);
  }
}

.ai-assistant-badge {
  display:                             inline-flex;
  align-items:                         center;
  gap:                                 6px;
  white-space:                         nowrap;

  img {
    display:                           block;
    flex-shrink:                       0;
  }

  span {
    color:                              var(--Shades-70);
    font-size:                          var(--font-size-10);
    font-weight:                        700;
    letter-spacing:                     1.5px;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import lgAutoplay from 'lightgallery/plugins/autoplay';
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from '@/assets/dependencies/lightGallery/plugins/zoom';

export default {
  name: 'ExamGridList',
  props: {
    table_exam: { type: Object, default: null },
    thumb_path: { type: String, default: '' },
    video_thumb_path: { type: String, default: '' },
    data_path: { type: String, default: '' },
    scale: { type: Number, default: 1 },
  },
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    session: null,
    lightGallerySettings: {
      showZoomInOutIcons: true,
      actualSize: false,
      autoplayVideoOnSlide: true,
      speed: 300,
      licenseKey: 'CE440F6C-34CE4C2A-86065648-48895C83',
      plugins: [],
      showMutirao: false,
      preload: 1,
      videojs: false
    },
    showMutirao: false,
    lightGallery: null,
    currentLgIndex: 0,
    currentOpenItem: null,
    showActionButtons: false,
    hasUnifiedReportFeature: false,
    isPaidService: false,
    hasAiAssistantFeature: false,
    isSpecialist: null,
    hasOthersExamData: null,
    MAX_GRID_THUMBNAILS: 30,
    isPowerfull: null,
    hoverVideoTimer: null,
    playingVideoIndex: null,
  }),
  created() {
    this.lightGallerySettings.plugins = [lgAutoplay, lgFullscreen, lgThumbnail, lgVideo, lgZoom]
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.showMutirao = this.session.features.includes('mutirao')
    this.hasUnifiedReportFeature = this.$utils.isUnifiedReportExperience(this.session.features)
    this.hasOthersExamData = this.session.features.includes('othersExamData')
    this.isPaidService = this.$utils.getIsPaidService()
    this.hasAiAssistantFeature = this.$utils.canUseAiAssistant(this.session)
    this.isSpecialist = this.session.isSpecialist
    this.isPowerfull = this.session.isPowerfull;
  },
  beforeUnmount() {
    clearTimeout(this.hoverVideoTimer)
  },
  methods: {
    aiStatusLabel(item) {
      const alterationText = item?.scoreResult?.alterationText
      if (alterationText !== undefined) {
        if (!alterationText.length) return this.$t('exam.notAltered')
        return this.$t(alterationText[0])
      }
      if (this.hasAiAssistantFeature && this.hasUnevaluableQuality(item)) return this.$t('exam.unevaluableQuality')
      return 'AI'
    },
    hasUnevaluableQuality(item) {
      const statuses = item?.scoreResult?.doubleCircle
        ? [item?.scoreResult?.odStatus, item?.scoreResult?.osStatus]
        : [item?.scoreResult?.odStatus]
      return statuses.every(status => status === 'no-info')
    },
    hasTypeStatusBadge(type) {
      return this.$utils.hasTypeStatusBadge(type)
    },
    badgeTypeLabel(type) {
      return this.$utils.badgeTypeLabel(this, type)
    },
    formatFaElapsedTime(image) {
      return this.$utils.getImageFaElapsedTime(image)
    },
    videoKey(item, imageIndex) {
      return `${item.uuid}_${imageIndex}`
    },
    onVideoHoverStart(item, imageIndex) {
      this.$utils.startHoverPreview(this, this.videoKey(item, imageIndex))
    },
    onVideoHoverEnd(item, imageIndex) {
      this.$utils.cancelHoverPreview(this, this.videoKey(item, imageIndex))
    },
    playHoverVideo(el) {
      this.$utils.playHoverVideo(el)
    },
    extraCustomization(item) {
      let self = this
      this.currentOpenItem = item
      this.currentLgIndex = 0

      $(".lg-download").off("click").on("click", function (event) {
        event.preventDefault();
        let examDataUUID = this.href.split('/').pop()
        let item = { uuid: examDataUUID }
        self.doDownload(item)
      })

      $('.lg-fullscreen.lg-icon').trigger('click');
    },
    onBeforeSlide(value) {
      this.currentLgIndex = value.index
      const laterality = this.getGalleryImages(this.currentOpenItem)[value.index]?.imageLaterality || ''
      this.$utils.lgUpdateLateralityDisplay(laterality)
    },
    async openConfirmLateralitySwitchOffcanvas(image) {
      if (!image || this.loading) return
      const deleteOriginal = await this.$refs.offcanvasGeneric.show({
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('examImages.confirmDeleteOriginal'),
        okButton: this.$i18n.t('yes'),
        okButtonClass: 'btn-danger',
        cancelButton: this.$i18n.t('no'),
        cancelButtonClass: 'btn-outline-secondary'
      })
      this.switchLaterality(image, deleteOriginal)
    },
    async switchLaterality(image, deleteOriginal) {
      if (!image || this.loading) return
      const laterality = image.imageLaterality === 'L' ? 'R' : 'L'
      await this.$helpers.requestHandling(async () => {
        await this.$examDataService.updateLaterality({ uuid: image.uuid, laterality, deleteOriginal })
        this.$emit('do-reload')
      }, this)
    },
    computeImageAiClass(image, exam) {
      return this.$aiService.computeImageAiClass(image, exam)
    },
    hasAiInfo(image, exam) {
      return this.$aiService.hasAiInfo(image, exam)
    },
    onLgClose() {
      this.$utils.lgClose()
    },
    getGalleryImages(item) {
      if (!item) return []
      const visible = item.examImages.filter(img => !img.type.startsWith('OTHER_') && img.type !== 'RNFL_RAW')
      // RNFL_PANO leads the lightbox (it's the full stitched panorama), immediately
      // followed by the RNFL_MASKED captures that originated it — RNFL_MASKED is hidden
      // from the grid via CSS (see .thumbnail-box.RNFL_MASKED) but still reachable by
      // navigating the opened lightbox, and keeping it right next to its pano instead of
      // wherever it fell in examImages avoids it reading as unrelated when an exam has
      // many images. An exam can carry more than one pano (one per eye), so pairing is
      // done via RNFL_PANO.metadata.childrenUUID (which lists the uuids of the raw/masked/
      // redfree captures used to generate it) rather than imageLaterality — this also
      // disambiguates the rarer case of two panos for the same eye, which laterality alone
      // couldn't.
      const usedMaskedUuids = new Set()
      const panoGroups = visible
        .filter(img => img.type === 'RNFL_PANO')
        .map(pano => {
          const childrenUUID = this.parseMetadata(pano)?.childrenUUID || []
          const siblings = visible.filter(img =>
            img.type === 'RNFL_MASKED' &&
            childrenUUID.includes(img.uuid) &&
            !usedMaskedUuids.has(img.uuid)
          )
          siblings.forEach(s => usedMaskedUuids.add(s.uuid))
          return [pano, ...siblings]
        })
      const rest = visible.filter(img =>
        img.type !== 'RNFL_PANO' && !(img.type === 'RNFL_MASKED' && usedMaskedUuids.has(img.uuid))
      )
      return [...panoGroups.flat(), ...rest].slice(0, this.MAX_GRID_THUMBNAILS)
    },
    parseMetadata(image) {
      if (!image?.metadata) return null
      try {
        return JSON.parse(image.metadata)
      } catch (err) {
        return null
      }
    },
    removeLoading(event) {
      let parentNode = event.target.parentElement.classList;
      let currentlyNode = event.target.classList;

      this.$nextTick(() => {
        currentlyNode.add('loaded')
        currentlyNode.remove('loading-eye')
        parentNode.remove('loading-eye')
      });
    },
    allowedImages(image) {
      return !image.endsWith('VIDEO')
    },
    prevent() {
    },
    formatDate(date) {
      return moment(date).format(this.$i18n.t('dateTimeFormat'))
    },
    isJokerPatient(item) {
      return item.patient.uuid == this.session.currentClinic.jokerPatientUuid
    },
    shouldDisableActionButtons(item) {
      return this.isJokerPatient(item) || !item.hasImage
    },
  async doDownload(item) {
      this.loading = true
      NProgress.start()
      try {
        let params = {}
        if (item.id)
          params['id'] = item.id

        if (item.uuid)
          params['uuid'] = item.uuid

        const url = this.$examDataService.getDownloadFileUrl(params)
        this.$utils.triggerFileDownload(url)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
      NProgress.done(true)
      this.loading = false
    },
    async doDownloadQuickReport(item) {
      this.loading = true
      NProgress.start()
      try {
        if (item.id)
          this.$examService.download(item.id)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    async checkDownloadOrRoute(item){
      if (item.hasReport){
        await this.doDownloadPDF(item)
      } else {
        this.$router.push({ name: 'exam-show', params: { examID: item.id }, query: { tab: 'report' } })
      }
    },
    async doDownloadPDF(item){
      let params = {
        id: item.id,
      }
      try {
        let response = await this.$reportService.getList(params);

        if (response.result.length > 0) {
          let theReport = response.result[response.result.length - 1]
          let params = {
            id: theReport.id
          }
          this.$reportService.generateReport(params);
        }

      } catch(err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
        // this.alert = this.$message.alert(this, 'request_error', 'danger')
      }
    },
    async setAlteration(value, item) {
      this.loading = true
      NProgress.start()
      try {
        if (value === 'notAltered') {
          let response = await this.$examService.autoGenerateReport({ examId: item.id, forceNotAltered: true })
          item.unifiedExam = response.exam.unifiedExam
          item.hasReport = response.exam.hasReport
          this.$emit('do-reload')
          return
        }

        let params = {
          examId: item.id,
          type: value
        }
        let response = await this.$examService.updateUnifiedExam(params)

        response.exam.unifiedExam.type == 'altered' ? this.goToReport(response.exam) : this.$emit('do-reload')
      } catch (e) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
        this.loading = false
      }
    },
    getVideoSource(item) {
      let result = {
        "source": [
          {
            "src": `${this.data_path}/${item.name}?autoplay=1`,
            "type": "video/mp4"
          }
        ],
        "attributes": {
          "preload": false,
          "playsinline": false,
          "controls": true,
          "loop": true
        }
      }

      return JSON.stringify(result)
    },
    onInit(detail) {
      this.lightGallery = detail.instance;
    },
    goToReport(item) {
      this.$emit('item-opened', { item, tab: 'report', query: { fromExamList: true } })
      // this.$router.push({ name: 'report', params: { examID: item.id, patient: item.patient } })
    },
    goToAiAssistant(item) {
      this.$emit('item-opened', { item, tab: 'aiAssistant' })
    },
    getAlterationClass(alteration) {
      if (typeof alteration === 'string') {
        if (alteration === 'altered') {
          return 'has-alteration'
        } else if (alteration === 'notAltered') {
          return 'no-alteration'
        } else if (alteration === 'noQuality') {
          return 'no-quality'
        }
      }
      return ''
    },
    async shareInformation(item) {
      const ok = await this.$refs.offcanvasPatientAccessInfo.show({
        patient: item.patient
      })
      if (ok) {
        NProgress.done(true)
      }
    },
    toPlans() {
      this.$router.push('/plans/')
    },
    handlePatientClick(item) {
      let params = {
        exam: item
      }

      // params.reassign = !this.isJokerPatient(item)

      if (this.isJokerPatient(item))
        this.$emit('do-assign', params)
      else
        this.$emit('show-patient', item)
    },
    canSwitchLaterality(item) {
      const excludedTypes = new Set(['STEREO', 'STEREO_IMAGE', 'STEREO_VIDEO', 'PANO', 'VIDEO', 'PROGRESSION_VIDEO']);
      return !excludedTypes.has(item.type);
    }
  }
}
</script>
